import { useNavigate } from 'react-router-dom';
import { Stack, Group, Text, Box } from '@repletoryreach/sdk-ui';
import { Tooltip } from '@repletoryreach/sdk-ui/overlays';
import type { templates } from '@repletoryreach/sdk-api';
import { formatDateTime } from '../../../lib/format';
import { TemplateStatusBadge } from './TemplateStatusBadge';
import { useT } from '../../../i18n/I18nProvider';

type Template = NonNullable<Awaited<ReturnType<typeof templates.list>>['data']>[number];

const MUTED = 'var(--rr-color-text-muted)';

export function TemplatesList({ records }: { records: Template[] }) {
  const navigate = useNavigate();
  const t = useT();
  return (
    <Stack gap="sm">
      {records.map((tpl) => (
        <button
          key={tpl.id}
          type="button"
          onClick={() => navigate(`/templates/${tpl.id}`)}
          className="rr-list-row"
        >
          <Group gap="md" align="center" wrap="nowrap" style={{ width: '100%' }}>
            <Stack gap="xs" style={{ flex: 1, minWidth: 0 }}>
              <Group gap="sm" align="baseline" wrap="nowrap">
                <Text role="subtitle" fw={600} truncate>{tpl.code}</Text>
                <Text role="caption" c={MUTED}>{tpl.category} · {tpl.language}</Text>
              </Group>
              <Text role="caption" c={MUTED}>
                {tpl.submitted_at ? t('templates.list.submitted', { at: formatDateTime(tpl.submitted_at) }) : t('status.draft')}
                {tpl.approved_at ? ` · ${t('templates.list.approved', { at: formatDateTime(tpl.approved_at) })}` : ''}
              </Text>
              {/* The rejection reason used to be concatenated into the line
                  above, where a long Meta reason was clipped with no way to
                  read it short of opening the row. */}
              {tpl.rejection_reason && (
                <Tooltip label={tpl.rejection_reason} position="top" openDelay={200}>
                  <Text role="caption" c="var(--rr-color-error)" truncate>
                    {t('templates.list.rejected', { reason: tpl.rejection_reason })}
                  </Text>
                </Tooltip>
              )}
            </Stack>
            <Box style={{ flexShrink: 0 }}>
              <TemplateStatusBadge status={tpl.status} devFallback={tpl.dev_fallback} metaStatus={tpl.meta_status} />
            </Box>
          </Group>
        </button>
      ))}
    </Stack>
  );
}
